/**
 * The two credit rules the client duplicates from the server.
 *
 * They live here, as pure functions, because both have already drifted from
 * their server-side counterparts in ways nothing caught:
 *
 * - The API exempts administrators from the balance check; the form did not,
 *   so an admin with a zero balance could not send the request the server was
 *   waiting to allow.
 * - The SPA sent `data_room` after the API had moved to `data_room_id`, and
 *   pydantic dropped it silently.
 *
 * A predicate inlined in a component cannot be tested without a DOM, a router
 * and a query client — which is why it never was. Out here it is four lines
 * and a table of cases, checked against exactly what `tests/test_credits.py`
 * asserts on the Python side.
 *
 * **The server remains the authority.** These decide what the *form* offers;
 * `POST /api/engagements` answers 402 regardless, and must keep doing so.
 */

import type { Depth } from "./types";

export type Role = "member" | "admin";

/**
 * What this run will reserve.
 *
 * An explicit ceiling wins over the depth default, which is how the server
 * resolves it — see `Engagement.cost_budget_usd`.
 *
 * `maxCost` is the raw form field, kept as a string so an empty box means
 * "use the depth default" rather than zero, which would read as an unspendable
 * run.
 */
export function reservesFor(
  maxCost: string,
  depth: Depth,
  byDepth: Record<Depth, number> | undefined,
  depthDefault: number | undefined,
): number {
  if (maxCost.trim() !== "") {
    const explicit = Number(maxCost);
    return Number.isFinite(explicit) && explicit > 0 ? explicit : 0;
  }
  return byDepth?.[depth] ?? depthDefault ?? 0;
}

/**
 * Whether the form should let this run be started.
 *
 * Two ways to be allowed, and both matter:
 *
 * - **An administrator always is.** They are the person paying the provider's
 *   bill, so there is nobody for them to buy credit from. The API says the
 *   same thing via `allow_overdraft`.
 * - **An unknown balance always is.** If `/api/credits` has not answered, the
 *   client has no business refusing — the server is the authority and answers
 *   402 if it disagrees. Blocking on missing data turns a slow request into a
 *   dead button.
 *
 * Only a *known* shortfall on a non-exempt account is refused.
 */
export function canAfford(
  role: Role | undefined,
  availableUsd: number | undefined,
  reservesUsd: number,
): boolean {
  if (role === "admin") return true;
  if (availableUsd === undefined) return true;
  return availableUsd >= reservesUsd;
}

/** What to top up by, for the message. Never negative. */
export function shortfall(availableUsd: number, reservesUsd: number): number {
  return Math.max(reservesUsd - availableUsd, 0);
}

/**
 * The most an admin may hand out in one manual grant.
 *
 * One engagement at the deepest depth, so a grant funds a whole run rather
 * than part of one. Duplicated from `MAX_MANUAL_GRANT_USD` in
 * `core/accounts/models.py` and bound to it by `tests/test_credits.py` so the
 * two cannot drift. The server refuses anything larger regardless; this only
 * decides what the form offers, so an admin is told before submitting rather
 * than after.
 */
export const MAX_MANUAL_GRANT_USD = 170;

/**
 * Whether this amount may be submitted.
 *
 * Zero moves nothing. Above the cap is refused. Negative is *not* capped: a
 * correction removes credit rather than creating it, and a refund has to be
 * able to match a purchase of any size.
 */
export function canGrant(amountUsd: number): boolean {
  if (!Number.isFinite(amountUsd) || amountUsd === 0) return false;
  return amountUsd <= MAX_MANUAL_GRANT_USD;
}
